import { type AffiliateCheckoutSession, createApiClient } from '@/services/api';
import { useAuth } from '@clerk/clerk-react';
import { useCallback, useMemo, useRef } from 'react';

/**
 * FEAT-APR08-002 — Coach permission flags.
 * Mirror of the backend CoachPermissions interface in services/coach-permissions.ts.
 * Must stay in sync — if new flags are added on the backend, add them here too.
 */
export interface CoachPermissions {
  /** Create Daily WODs, Standards, Specialty workouts. DEFAULT: ON per Des 2026-04-08. */
  can_create_workouts: boolean;
  can_invite: boolean;
  can_create_events: boolean;
  /** Requires score approval schema (migration 0054). */
  can_approve_scores: boolean;
  /** Read-only billing view; does not grant write access. */
  can_view_billing: boolean;
  can_manage_coaches: boolean;
  can_manage_members: boolean;
  can_log_scores_for_members: boolean;
  can_post_announcements: boolean;
  /** Depends on STORY-STRIPE-011 promo code infrastructure. */
  can_offer_discounts: boolean;
}

export const COACH_PERMISSION_KEYS: (keyof CoachPermissions)[] = [
  'can_create_workouts',
  'can_invite',
  'can_create_events',
  'can_approve_scores',
  'can_view_billing',
  'can_manage_coaches',
  'can_manage_members',
  'can_log_scores_for_members',
  'can_post_announcements',
  'can_offer_discounts',
];

export const COACH_PERMISSION_LABELS: Record<keyof CoachPermissions, string> = {
  can_create_workouts: 'Create workouts',
  can_invite: 'Invite members',
  can_create_events: 'Create events',
  can_approve_scores: 'Approve scores',
  can_view_billing: 'View billing (read-only)',
  can_manage_coaches: 'Manage coaches',
  can_manage_members: 'Manage members (edit / suspend)',
  can_log_scores_for_members: 'Log scores for members',
  can_post_announcements: 'Post announcements',
  can_offer_discounts: 'Offer member discounts',
};

/**
 * Hook that provides affiliates API methods with proper Clerk token handling.
 * Uses refs and callbacks to ensure stable function references across renders.
 */
export function useAffiliatesApi() {
  const { getToken } = useAuth();

  // Store getToken in a ref to avoid dependency changes
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Create a stable token getter that always uses the latest getToken
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  const affiliatesApi = useMemo(() => {
    const client = createApiClient(stableGetToken);

    return {
      // FEAT-APR08-004 — optional ?scope=all for portfolio view across
      // every gym the user owns, or ?affiliate_id=<uuid> for a specific gym.
      getDashboard: (params?: { scope?: 'all'; affiliateId?: string }) => {
        const qs = new URLSearchParams();
        if (params?.scope) qs.set('scope', params.scope);
        if (params?.affiliateId) qs.set('affiliate_id', params.affiliateId);
        const suffix = qs.toString() ? `?${qs}` : '';
        return client.get<{
          affiliate: { id: string; name: string; status: string };
          billing_state?: 'pending_approval' | null;
          metrics: {
            total_members: number;
            active_members: number;
            scores_this_week: number;
          };
          gyms?: Array<{ id: string; name: string; status: string }>;
        }>(`/affiliates/dashboard${suffix}`);
      },

      getMembers: (params?: {
        page?: number;
        limit?: number;
        search?: string;
      }) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', params.page.toString());
        if (params?.limit) searchParams.set('limit', params.limit.toString());
        if (params?.search) searchParams.set('search', params.search);
        return client.get<{
          members: Array<{
            id: string;
            name: string;
            email: string;
            status: string;
            joined_at: string;
          }>;
          pagination: { page: number; limit: number; total: number };
        }>(`/affiliates/members?${searchParams}`);
      },

      inviteMember: (email: string, name?: string) =>
        client.post<{ member: { id: string; email: string } }>('/affiliates/members/invite', {
          email,
          name,
        }),

      bulkInvite: (emails: string[]) =>
        client.post<{
          summary: { total: number; success: number; failed: number; emails_sent: number };
          results: Array<{ email: string; success: boolean; error?: string; id?: string }>;
          invited?: number;
          failed?: number;
        }>('/affiliates/members/invite/bulk', { emails }),

      getProgramming: (params?: { month?: string; year?: number }) => {
        const searchParams = new URLSearchParams();
        if (params?.month) searchParams.set('month', params.month);
        if (params?.year) searchParams.set('year', params.year.toString());
        return client.get<{
          workouts: Array<{
            id: string;
            name: string;
            description: string;
            scheduled_date: string;
            track: { id: string; name: string };
            movements?: string;
            scoring_type?: string;
            scoring_direction?: string;
            equipment?: string;
            category?: string | null;
            workout_type?: string | null;
            duration_minutes?: number | null;
            coach_notes?: string | null;
            instructions?: string | null;
          }>;
          tracks?: Array<{ id: string; name: string }>;
        }>(`/affiliates/programming?${searchParams}`);
      },

      getWorkoutTV: (workoutId: string) =>
        client.get<{
          workout: { id: string; name: string; description: string };
          leaderboard: Array<{
            rank: number;
            user_name: string;
            score_value: number;
            score_unit: string;
          }>;
        }>(`/affiliates/programming/${workoutId}/tv`),

      getEvents: () =>
        client.get<{
          events: Array<{
            id: string;
            name: string;
            event_date: string;
            participant_count: number;
            max_participants: number;
          }>;
        }>('/affiliates/events'),

      createEvent: (
        data: {
          name: string;
          description?: string;
          event_date: string;
          max_participants?: number;
          visibility?: 'internal' | 'public';
        },
        idempotencyKey?: string
      ) =>
        client.post<{ event: { id: string; name: string } }>('/affiliates/events', data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      getEventDetails: (eventId: string) =>
        client.get<{
          event: {
            id: string;
            name: string;
            description: string | null;
            event_date: string;
            format: string | null;
            max_participants: number | null;
            status: string;
          };
          participants: Array<{
            id: string;
            user_id: string;
            name: string;
            email: string;
            signed_up_at: string;
          }>;
        }>(`/affiliates/events/${eventId}`),

      deleteEvent: (eventId: string) =>
        client.delete<{ message: string; status?: string }>(`/affiliates/events/${eventId}`),

      getLeaderboard: (workoutId: string) =>
        client.get<{
          leaderboard: Array<{
            rank: number;
            user_id: string;
            user_name: string;
            score_value: number;
            score_unit: string;
          }>;
        }>(`/affiliates/leaderboard?workout_id=${workoutId}`),

      getBillingPortal: () => client.post<{ url: string }>('/affiliates/billing/portal'),

      createCheckoutSession: () =>
        client.post<AffiliateCheckoutSession>('/affiliates/billing/checkout'),

      // Team Management (coaches/staff)
      getTeam: () =>
        client.get<{
          team: Array<{
            id: string;
            user_id: string;
            name: string;
            email: string;
            role: 'affiliate_owner' | 'affiliate_coach';
            is_active: boolean;
            invite_status: 'invited' | 'active' | 'suspended';
            created_at: string;
          }>;
          stats: {
            total: number;
            active: number;
            invited: number;
          };
        }>('/affiliates/team'),

      // FEAT-APR08-007 — multi-gym owners can target a specific gym from
      // their portfolio by passing affiliateId; the backend verifies ownership.
      inviteCoach: (email: string, name?: string, affiliateId?: string) =>
        client.post<{ coach: { id: string; email: string }; message: string }>(
          '/affiliates/team/invite',
          { email, name, ...(affiliateId ? { affiliate_id: affiliateId } : {}) }
        ),

      removeCoach: (userId: string) =>
        client.delete<{ message: string }>(`/affiliates/team/${userId}`),

      updateCoachAccess: (userId: string, isActive: boolean) =>
        client.patch<{ message: string }>(`/affiliates/team/${userId}`, { is_active: isActive }),

      // Tag Management
      getTags: () =>
        client.get<{
          tags: Array<{
            id: string;
            tag: string;
            color: string;
            created_at: string;
            member_count: number;
          }>;
        }>('/affiliates/tags'),

      createTag: (tag: string, color?: string) =>
        client.post<{ id: string; tag: string; color: string }>('/affiliates/tags', { tag, color }),

      deleteTag: (tagId: string) => client.delete<{ message: string }>(`/affiliates/tags/${tagId}`),

      getMemberTags: (memberId: string) =>
        client.get<{
          tags: Array<{ id: string; tag: string; created_at: string }>;
        }>(`/affiliates/tags/members/${memberId}`),

      addMemberTag: (memberId: string, tag: string) =>
        client.post<{ id: string; tag: string }>(`/affiliates/tags/members/${memberId}`, { tag }),

      removeMemberTag: (memberId: string, tagId: string) =>
        client.delete<{ message: string }>(`/affiliates/tags/members/${memberId}/${tagId}`),

      getCoachPermissions: (memberId: string) =>
        client.get<{
          permissions: CoachPermissions;
        }>(`/affiliates/members/${memberId}/permissions`),

      updateCoachPermissions: (
        memberId: string,
        update: Partial<CoachPermissions> | { preset: 'junior_coach' | 'gym_manager' }
      ) =>
        client.patch<{ permissions: CoachPermissions }>(
          `/affiliates/members/${memberId}/permissions`,
          update
        ),

      // FEAT-APR08-002: single-call team overview for the Team Management page.
      // Returns members + permissions + freemium quota state.
      getTeamManagement: () =>
        client.get<{
          affiliate: { id: string; name: string; subscription_status: string | null };
          people: Array<{
            member_id: string;
            user_id: string;
            email: string;
            name: string;
            member_role: 'owner' | 'coach' | 'member';
            is_active: boolean;
            created_at: string;
            permissions: CoachPermissions | null;
          }>;
          quota: {
            is_frozen: boolean;
            owners: { count: number; cap: number | null };
            coaches: { count: number; cap: number | null };
            members: { count: number; cap: number | null };
          };
        }>('/affiliates/team/management'),

      // Join request management (owner only)
      getJoinRequests: () =>
        client.get<{
          requests: Array<{
            id: string;
            user_id: string;
            name: string;
            email: string;
            status: string;
            message: string | null;
            requested_role: string | null;
            request_type: string | null;
            assigned_by_name: string | null;
            created_at: string;
            reviewed_at: string | null;
            rejection_reason: string | null;
          }>;
          pending_count: number;
        }>('/affiliates/join-requests'),

      handleJoinRequest: (
        requestId: string,
        action: 'approve' | 'reject',
        rejectionReason?: string
      ) =>
        client.patch<{ message: string }>(`/affiliates/join-requests/${requestId}`, {
          action,
          rejection_reason: rejectionReason,
        }),

      createWorkout: (
        data: {
          name: string;
          description?: string;
          scheduled_date: string;
          scoring_type: string;
          scoring_direction?: string;
          movements?: string;
          equipment?: string;
          workout_type?: string;
          publish_to_gym?: boolean;
          shared_to_global?: boolean;
          is_favorite?: boolean;
        },
        idempotencyKey?: string
      ) =>
        client.post<{ id: string; name: string }>('/affiliates/programming', data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      updateWorkout: (
        workoutId: string,
        data: {
          name: string;
          description?: string;
          scheduled_date: string;
          scoring_type: string;
          scoring_direction?: string;
          movements?: string;
          equipment?: string;
          is_favorite?: boolean;
        },
        idempotencyKey?: string
      ) =>
        client.patch<{ id: string; name: string }>(`/affiliates/programming/${workoutId}`, data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      createWorkoutOverride: (
        workoutId: string,
        data: {
          name?: string;
          description?: string;
          movements?: string;
        },
        idempotencyKey?: string
      ) =>
        client.post<{ id: string; name: string }>(
          `/affiliates/programming/${workoutId}/override`,
          data,
          { headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined }
        ),

      // Member Role & Removal
      changeMemberRole: (memberId: string, role: string) =>
        client.patch<{
          member_id: string;
          previous_role: string;
          new_role: string;
          message: string;
        }>(`/affiliates/members/${memberId}/role`, { role }),

      removeMember: (memberId: string) =>
        client.delete<{ message: string }>(`/affiliates/members/${memberId}`),
    };
  }, [stableGetToken]);

  return affiliatesApi;
}
